import React, { Fragment, ReactNode } from "react";
import { cn } from "@/lib/utils";
import { stegaClean } from "@sanity/client/stega";

/* ── Quarterly Event shared kit ──────────────────────────────────────────────
   Figma: RTqa1425vVOUAEVlKfKcnt (desktop 12375:8072, mobile 12405:6602). 1rem = Figma px / 8.
   Colours: mint #9ee3b0 · green #51a667 · handle #7cc493 · ink #111112. */

export const MINT = "#9ee3b0";
export const GREEN = "#51a667";
export const HANDLE = "#7cc493";

/** Display face (Helvetica Neue LT Std 65 Medium), Figma tracking -3%. */
export const HN = "font-helvetica-lt font-medium tracking-[-0.03em]";
export const CAP_TRIM = "[text-box-trim:trim-both] [text-box-edge:cap_alphabetic]";

export const ctaHref = (url?: string) => stegaClean(url || "") || "#signup";

/** The spinning 3D medal used in every eyebrow chip and the top bar (29×34 Figma px). */
export function QeMedal({ className }: { className?: string }) {
  return (
    <span aria-hidden="true" className={cn("relative block h-[4.25rem] w-[3.625rem] shrink-0 [perspective:27.5rem]", className)}>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src="/quarterly-event/medal.png" alt="" className="qe-medal absolute inset-0 h-full w-full object-contain [transform-style:preserve-3d] motion-reduce:animate-none" />
    </span>
  );
}

/** Eyebrow chip: medal + uppercase label (Geist 18/24, -1%). `dark` on black sections. */
export function QeChip({ label, tone = "light", className }: { label?: string; tone?: "light" | "dark"; className?: string }) {
  if (!label) return null;
  return (
    <span className={cn("inline-flex items-center gap-[1.75rem] whitespace-nowrap font-geist text-[2.25rem] leading-[1.333] font-medium tracking-[-0.01em] uppercase", tone === "dark" ? "text-white" : "text-[#0b0b0b]", className)}>
      <QeMedal />
      <span className="font-geist">{label}</span>
    </span>
  );
}

/**
 * Title text with the Figma selection highlight: `[[words]]` get the tinted box with a bar + nub
 * handle on each side; `\n` breaks the line. `dark` = mint on black, `light` = black on white.
 */
export function QeHighlightText({ text, tone = "light" }: { text?: string; tone?: "light" | "dark" }) {
  if (!text) return null;
  const lines = text.split("\n");
  return (
    <>
      {lines.map((line, li) => (
        <Fragment key={li}>
          {li > 0 && <br />}
          {line.split(/(\[\[[^\]]+\]\])/g).map((part, pi) => {
            const m = part.match(/^\[\[([^\]]+)\]\]$/);
            if (!m) return <Fragment key={pi}>{part}</Fragment>;
            return (
              <span key={pi} className={cn("qe-hl", tone === "dark" ? "qe-hl--dark" : "qe-hl--light")}>
                <i className="l" aria-hidden="true" />
                {m[1]}
                <i className="r" aria-hidden="true" />
              </span>
            );
          })}
        </Fragment>
      ))}
    </>
  );
}

/** Section title, Figma 52/60 HN medium by default. */
export function QeTitle({ text, tone = "light", as: Tag = "h2", className }: { text?: string; tone?: "light" | "dark"; as?: "h1" | "h2" | "h3"; className?: string }) {
  if (!text) return null;
  return (
    <Tag className={cn(HN, "mb-0 text-[6.5rem] leading-[1.1538]", tone === "dark" ? "text-white" : "text-black", className)}>
      <QeHighlightText text={text} tone={tone} />
    </Tag>
  );
}

/** Chip → title → description, centred, gaps 16px. */
export function QeSectionHead({ chip, title, text, tone = "light", textClassName, titleClassName, className, align = "center" }: {
  chip?: string; title?: string; text?: string; tone?: "light" | "dark"; textClassName?: string; titleClassName?: string; className?: string; align?: "center" | "left";
}) {
  return (
    <div className={cn("flex flex-col gap-[2rem]", align === "center" ? "items-center text-center" : "items-start text-left", className)}>
      <QeChip label={chip} tone={tone} />
      <QeTitle text={title} tone={tone} className={titleClassName} />
      {text && (
        <p className={cn("mb-0 font-geist text-[2.25rem] leading-[1.5] whitespace-pre-line", tone === "dark" ? "text-white/80" : "text-black/70", textClassName)}>{text}</p>
      )}
    </div>
  );
}

/** The green CTA: 54px pill + 57×54 icon tile. Label rolls up and the arrow slides out on hover. */
export function QeCta({ label, href, className, mainClassName, submit, disabled }: { label?: string; href?: string; className?: string; mainClassName?: string; submit?: boolean; disabled?: boolean }) {
  if (!label) return null;
  const cls = cn("qe-cta group inline-flex items-center gap-[0.25rem] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#9ee3b0]", className);
  const inner = (
    <>
      <span className={cn("relative isolate inline-flex h-[6.75rem] items-center justify-center overflow-hidden rounded-[0.375rem] bg-[#9ee3b0] px-[3rem] font-geist text-[2rem] leading-[1.3125] font-semibold whitespace-nowrap text-black transition-colors duration-200 group-hover:bg-[#b6edc4]", mainClassName)}>
        <span className="qe-cta-roll block h-[2.625rem] overflow-hidden">
          <span className="block h-[2.625rem] font-geist leading-[2.625rem]">{label}</span>
          <span aria-hidden="true" className="block h-[2.625rem] font-geist leading-[2.625rem]">{label}</span>
        </span>
      </span>
      <svg className="qe-cta-ico h-[6.75rem] w-[7.125rem] shrink-0 overflow-hidden" viewBox="0 0 57 54" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
        <path d="M0 4C0 1.79086 1.79086 0 4 0H52.64C54.8491 0 56.64 1.79086 56.64 4V50C56.64 52.2091 54.8491 54 52.64 54H4C1.79086 54 0 52.2091 0 50V4Z" fill="#9EE3B0" />
        <rect x="3.32007" y="3.31982" width="50" height="47.36" rx="2.44" fill="black" />
        <path className="qe-cta-arrow" d="M21 20.2866V29.8656H36M31.5 34.2866L36 29.8656L31.5 25.4445" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" stroke="#9EE3B0" />
      </svg>
    </>
  );
  return submit ? (
    <button type="submit" disabled={disabled} className={cn(cls, "cursor-pointer appearance-none border-0 bg-transparent p-0 disabled:cursor-wait disabled:opacity-70")}>{inner}</button>
  ) : (
    <a href={ctaHref(href)} className={cls}>{inner}</a>
  );
}

/** Page system lines (x=178 / x=1740 at 1920) + optional hatched side bands, desktop only. */
export function QeRails({ hatch = false, tone = "light", className }: { hatch?: boolean; tone?: "light" | "dark"; className?: string }) {
  const line = tone === "dark" ? "bg-white/[0.24]" : "bg-[rgba(128,128,128,0.3)]";
  return (
    <div aria-hidden="true" className={cn("pointer-events-none absolute inset-0 hidden lg:block", className)}>
      {hatch && (
        <>
          <i className="absolute inset-y-0 left-0 w-[22.375rem] bg-[url('/quarterly-event/gutter-hatch.png')] bg-[length:1rem_1rem] opacity-60" />
          <i className="absolute inset-y-0 right-0 w-[22.375rem] bg-[url('/quarterly-event/gutter-hatch.png')] bg-[length:1rem_1rem] opacity-60" />
        </>
      )}
      <i className={cn("absolute inset-y-0 left-[22.25rem] w-px", line)} />
      <i className={cn("absolute inset-y-0 right-[22.375rem] w-px", line)} />
    </div>
  );
}

/** Keyframes + the highlight box; render once in the page. */
export function QeStyles() {
  return (
    <style
      dangerouslySetInnerHTML={{
        __html: `
@media (max-width:639px){:root:root{font-size:calc(800svw / 393)}}
.qe-hl{position:relative;z-index:0;isolation:isolate;display:inline-block;padding:0 1.25rem;margin:0 .25rem;color:var(--hc)}
.qe-hl::before{content:"";position:absolute;inset:0;background:var(--hb);z-index:-1}
.qe-hl i{position:absolute;top:0;bottom:0;width:1.25rem;pointer-events:none}
.qe-hl i::before,.qe-hl i::after{content:"";position:absolute;background:var(--ha)}
.qe-hl .r{right:-1rem}.qe-hl .r::before{left:0;width:.25rem;top:0;bottom:0}
.qe-hl .r::after{left:0;top:0;width:1.25rem;height:22%;border-radius:.375rem .375rem 0 0}
.qe-hl .l{left:-1rem}.qe-hl .l::before{right:0;width:.25rem;top:0;bottom:0}
.qe-hl .l::after{right:0;bottom:0;width:1.25rem;height:22%;border-radius:0 0 .375rem .375rem}
.qe-hl--dark{--hc:#9ee3b0;--hb:rgba(158,227,176,.16);--ha:#9ee3b0}
.qe-hl--light{--hc:#000;--hb:rgba(81,166,103,.07);--ha:#7cc493}
@keyframes qe-medspin{0%,18%{transform:rotateY(0)}62%,100%{transform:rotateY(360deg)}}
.qe-medal{animation:qe-medspin 4.6s cubic-bezier(.65,0,.35,1) infinite}
/* hover loops: the motion opens the 1.6 s cycle (ease-out), so it answers the pointer at once, then holds */
@keyframes qe-ctaroll{0%{transform:none}25%,100%{transform:translateY(-100%)}}
@keyframes qe-ctaarrow{0%{transform:translate(0,0);opacity:1}11%{transform:translate(160%,0);opacity:0}11.1%{transform:translate(-160%,0);opacity:0}24%,100%{transform:translate(0,0);opacity:1}}
.qe-cta-arrow{transform-box:fill-box}
.qe-cta:hover .qe-cta-roll span{animation:qe-ctaroll 1.6s cubic-bezier(.22,1,.36,1) infinite}
.qe-cta:hover .qe-cta-arrow{animation:qe-ctaarrow 1.6s cubic-bezier(.22,1,.36,1) infinite}
@keyframes qe-ticker{to{transform:translateX(-50%)}}
.qe-rv{opacity:0;translate:0 2.25rem;transition:opacity .9s cubic-bezier(.16,1,.3,1),translate .9s cubic-bezier(.16,1,.3,1);transition-delay:var(--d,0ms)}
.qe-rv.qe-in{opacity:1;translate:0 0}
@media (prefers-reduced-motion:reduce){.qe-rv{opacity:1;translate:none;transition:none}.qe-medal,.qe-cta-roll span,.qe-cta-arrow{animation:none!important}}
`,
      }}
    />
  );
}

export function QeWrap({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("relative mx-auto w-[45.125rem] lg:w-[150rem]", className)}>{children}</div>;
}
